import { useState, useRef } from 'react';
import { Conversation, Message, User } from '../types';
import { ConversationsService } from '../services/conversationsService';

/**
 * Hook ultra-simplificado para conversaciones
 * SIN bucles infinitos - carga solo cuando se llama explícitamente
 */
export const useConversations = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Control absoluto contra bucles
  const hasLoadedRef = useRef(false);
  const loadingUserRef = useRef<string | null>(null);

  /**
   * Carga conversaciones SOLO cuando se llama explícitamente
   * NO se ejecuta automáticamente
   */
  const loadConversations = async (userId: string): Promise<void> => {
    // PROTECCIÓN ABSOLUTA contra bucles
    if (hasLoadedRef.current || loadingUserRef.current === userId || isLoading) {
      console.log('🛑 CARGA BLOQUEADA - Ya ejecutada o en progreso');
      return;
    }

    console.log('🔄 CARGA ÚNICA de conversaciones iniciada para:', userId);
    
    // Marcar como cargando INMEDIATAMENTE
    hasLoadedRef.current = true;
    loadingUserRef.current = userId;
    setIsLoading(true);
    setError('');

    try {
      const result = await ConversationsService.getUserConversations(userId);
      
      if (result.success && result.conversations) {
        console.log(`✅ ${result.conversations.length} conversaciones cargadas exitosamente`);
        
        const basicConversations = result.conversations.map(conv => ({
          id: conv.id,
          participants: [], // Se llenarán cuando se acceda a la conversación específica
          messages: [],
          updatedAt: new Date(conv.updated_at),
          participant1_id: conv.participant1_id,
          participant2_id: conv.participant2_id
        }));
        
        console.log('📋 Conversaciones procesadas para mostrar:', basicConversations.map(c => ({
          id: c.id,
          participant1_id: c.participant1_id,
          participant2_id: c.participant2_id,
          updatedAt: c.updatedAt
        })));
        
        setConversations(basicConversations);
      } else {
        console.log('📭 No hay conversaciones disponibles');
        setConversations([]);
        setError(result.error || '');
      }
    } catch (error) {
      console.error('❌ Error al cargar conversaciones:', error);
      setConversations([]);
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
      loadingUserRef.current = null;
      console.log('🏁 Carga de conversaciones finalizada');
    }
  };

  /**
   * Obtiene una conversación por ID con datos de usuarios cargados
   */
  const getConversationById = async (conversationId: string, currentUserId: string): Promise<Conversation | undefined> => {
    console.log('🔍 Buscando conversación por ID:', conversationId);
    
    // Primero buscar en conversaciones cargadas
    const existingConversation = conversations.find(conv => conv.id === conversationId);
    if (existingConversation && existingConversation.participants.length > 0) {
      console.log('✅ Conversación encontrada en memoria');
      return existingConversation;
    }

    // Si no está en memoria, cargar conversación completa desde Supabase
    try {
      console.log('📨 Cargando conversación completa desde Supabase:', conversationId);
      
      // Primero obtener los datos de la conversación
      const conversationResult = await ConversationsService.findConversationById(conversationId);
      
      if (!conversationResult.success || !conversationResult.conversation) {
        console.error('❌ Error al cargar datos de conversación:', conversationResult.error);
        return undefined;
      }

      const convData = conversationResult.conversation;
      console.log('📋 Datos de conversación obtenidos:', {
        id: convData.id,
        participant1_id: convData.participant1_id,
        participant2_id: convData.participant2_id
      });

      // Cargar usuarios participantes
      const [user1Result, user2Result] = await Promise.all([
        ConversationsService.getUserById(convData.participant1_id),
        ConversationsService.getUserById(convData.participant2_id)
      ]);

      if (!user1Result.success || !user2Result.success) {
        console.error('❌ Error al cargar usuarios participantes');
        return undefined;
      }

      const participants = [user1Result.user!, user2Result.user!];
      console.log('👥 Participantes cargados:', participants.map(p => ({ id: p.id, name: p.name })));

      // Cargar mensajes
      const messagesResult = await ConversationsService.getConversationMessages(conversationId);
      
      const messages = messagesResult.messages || [];
      console.log(`✅ ${messages.length} mensajes cargados`);
      
      // Crear conversación completa
      const conversation: Conversation = {
        id: conversationId,
        participants,
        messages,
        lastMessage: messages.length > 0 ? messages[messages.length - 1] : undefined,
        updatedAt: new Date(convData.updated_at),
        participant1_id: convData.participant1_id,
        participant2_id: convData.participant2_id
      };

      console.log('✅ Conversación completa creada:', {
        id: conversation.id,
        participantsCount: conversation.participants.length,
        messagesCount: conversation.messages.length
      });

      return conversation;
      
    } catch (error) {
      console.error('❌ Error al cargar conversación:', error);
      return undefined;
    }
  };

  /**
   * Resetea el estado para permitir nueva carga
   */
  const resetLoadState = (): void => {
    hasLoadedRef.current = false;
    loadingUserRef.current = null;
    setIsLoading(false);
    setError('');
    console.log('🔄 Estado de carga reseteado');
  };

  return {
    conversations,
    isLoading,
    error,
    hasLoaded: hasLoadedRef.current,
    loadConversations,
    getConversationById,
    resetLoadState
  };
};